import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Page, Card, Link, Badge, Modal, Banner, BlockStack, Text } from '@shopify/polaris';
import { getRequest } from '../../shared/components/functions.jsx';
import InvoiceSelect from '../../shared/components/invoiceSelect.jsx';
import { TabTitle } from '../../shell/helmet';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

const STATUS_BADGES = {
  active: <Badge tone="success">Active</Badge>,
  paused: <Badge tone="warning">Paused</Badge>,
  ended: <Badge>Ended</Badge>
};

export default function RecurringInvoicesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fromInvoiceModalOpen, setFromInvoiceModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    const data = await getRequest('/api/recurring_invoices');
    if (Array.isArray(data)) setItems(data);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createFromInvoice = async () => {
    if (!selectedInvoiceId) return;
    setCreating(true);
    const response = await fetch(`/api/recurring_invoices/from-invoice/${selectedInvoiceId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    setCreating(false);
    if (response.ok) {
      const created = await response.json();
      navigate(`/recurring_invoices/${created.RecurringInvoiceID}`);
    } else {
      alert('Could not create recurring invoice from the selected invoice.');
    }
  };

  const rows = items.map((item) => [
    <Link removeUnderline url={`/recurring_invoices/${item.RecurringInvoiceID}`}>
      {item.Name}
    </Link>,
    <Link removeUnderline url={`/companies/${item.klantnummer}`}>
      {item.company_name}
    </Link>,
    `${FREQUENCY_LABELS[item.Frequency] || item.Frequency}${item.IntervalCount > 1 ? ` (every ${item.IntervalCount})` : ''}`,
    item.NextRunDate ? String(item.NextRunDate).substring(0, 10) : '-',
    item.LastRunDate ? String(item.LastRunDate).substring(0, 10) : '-',
    item.generated_count || 0,
    item.failed_count > 0 ? <Badge tone="critical">{`${item.failed_count} failed`}</Badge> : '',
    item.notifier_name ? <Badge>{item.notifier_name}</Badge> : <Badge tone="warning">No notifier</Badge>,
    item.business_unit_code ? <Badge>{item.business_unit_code}</Badge> : '',
    STATUS_BADGES[item.Status] || item.Status
  ]);

  return (
    <Page
      title="Recurring invoices"
      fullWidth
      primaryAction={{ content: 'New recurring invoice', onAction: () => navigate('/recurring_invoices/new') }}
      secondaryActions={[{ content: 'New from existing invoice', onAction: () => setFromInvoiceModalOpen(true) }]}
    >
      <TabTitle title="Recurring invoices" />
      <BlockStack gap="400">
        <Banner tone="info" title="Invoices generated from these templates are drafts">
          They are never sent to the customer automatically. The notifier receives an email and a human must review and send each invoice through the normal invoice flow.
        </Banner>
        <Card>
          <DataTable
            stickyHeader
            increasedTableDensity
            verticalAlign="middle"
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
            headings={[<b>Name</b>, <b>Company</b>, <b>Frequency</b>, <b>Next run</b>, <b>Last run</b>, <b>Generated</b>, <b>Failures</b>, <b>Notifier</b>, <b>BU</b>, <b>Status</b>]}
            rows={rows}
            footerContent={`${rows.length} recurring invoice template(s)`}
          />
        </Card>
      </BlockStack>

      <Modal
        open={fromInvoiceModalOpen}
        onClose={() => setFromInvoiceModalOpen(false)}
        title="New recurring invoice from an existing invoice"
        primaryAction={{ content: 'Create template', onAction: createFromInvoice, loading: creating, disabled: !selectedInvoiceId }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setFromInvoiceModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              The customer and all lines of the selected invoice will be copied into a new recurring invoice template. The template starts paused so you can review the schedule first.
            </Text>
            <InvoiceSelect id="fromInvoice" label="Invoice" value={selectedInvoiceId} onChange={(value) => setSelectedInvoiceId(value)} />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
